import { FormValidateInput } from "@mantine/form";
import { TFunction } from "i18next";

import {
  DATE_ORDER_ERROR_KEY,
  DEFAULT_REGISTER_RANGE_DAYS,
  IDateRange,
  IReceiptFilters,
  isOrderedRange,
  isWithinMaxRange,
  MAX_REGISTER_RANGE_DAYS,
  RANGE_TOO_LONG_ERROR_KEY,
  TIME_ORDER_ERROR_KEY,
  toRecentRange,
} from "@prostocheck/shared";

import { TRegisterRangeValues } from "./use-register-filters.models";

/**
 * The dates are not just a filter — they are the query, for both sections of
 * the page. Every receipt costs a document call (ai/docs/prro-api.md), so the
 * screen starts on a couple of days rather than fetching whatever exists.
 */
export const toDefaultRegisterFilters = (): IReceiptFilters => {
  const { from, to } = toRecentRange(DEFAULT_REGISTER_RANGE_DAYS);

  return { dateFrom: from, dateTo: to, timeFrom: "", timeTo: "", type: "all" };
};

/**
 * Times only bound a single day: over two days or more, "from 22:00 to 06:00"
 * is an ordinary night shift rather than a mistake.
 */
export const isOrderedDayTimes = ({
  dateFrom,
  dateTo,
  timeFrom,
  timeTo,
}: TRegisterRangeValues): boolean =>
  dateFrom !== dateTo || !timeFrom || !timeTo || timeFrom <= timeTo;

/**
 * Order first, then span: an out-of-order pair is the more basic mistake, and
 * fixing it may well fix the span too, so there is never a reason to show
 * both at once.
 */
const validateDateBound = (range: IDateRange, t: TFunction): string | null => {
  if (!isOrderedRange(range)) return t(DATE_ORDER_ERROR_KEY);
  if (!isWithinMaxRange(range, MAX_REGISTER_RANGE_DAYS))
    return t(RANGE_TOO_LONG_ERROR_KEY, { count: MAX_REGISTER_RANGE_DAYS });

  return null;
};

/**
 * Both ends carry the rule, so the message lands on whichever field the user
 * is holding when the period stops making sense.
 */
export const createRegisterFiltersValidation = (
  t: TFunction,
): FormValidateInput<IReceiptFilters> => ({
  dateFrom: (value, { dateTo }) =>
    validateDateBound({ from: value, to: dateTo }, t),
  dateTo: (value, { dateFrom }) =>
    validateDateBound({ from: dateFrom, to: value }, t),
  timeFrom: (value, { dateFrom, dateTo, timeTo }) =>
    isOrderedDayTimes({ dateFrom, dateTo, timeFrom: value, timeTo })
      ? null
      : t(TIME_ORDER_ERROR_KEY),
  timeTo: (value, { dateFrom, dateTo, timeFrom }) =>
    isOrderedDayTimes({ dateFrom, dateTo, timeFrom, timeTo: value })
      ? null
      : t(TIME_ORDER_ERROR_KEY),
});
